/**
 * Von der Maschinen-Begründung zum Satz, den Nahm lesen kann.
 *
 * WOFÜR: Bis zum 2026-08-06 stand im Panel wörtlich, was der Server gemeldet
 * hatte — `staging_check_failed: …`, `push failed: …`, `main is 3 commits ahead
 * of staging`, dazu der Fehlertext des Bau-Programms samt Ablaufprotokoll. Nahm
 * arbeitet auf Thai und Englisch und ist keine Entwicklerin; damit konnte sie
 * nichts anfangen und musste jedes Mal nachfragen.
 *
 * Der technische Text geht deshalb nicht verloren, er steht nur woanders: im
 * Protokoll und hinter „Technische Einzelheiten".
 *
 * Jeder Satz beantwortet zwei Fragen: Was ist mit der Website, und was kann ich
 * jetzt tun.
 */

/**
 * Übersetzungsschlüssel für eine gescheiterte Veröffentlichung.
 *
 * Der Vergleich läuft über den Anfang der Begründung, weil der Server hinter
 * den Doppelpunkt die technische Ursache hängt.
 */
export function veroeffentlichenSchluessel(grund: string | null | undefined): string {
  if (!grund) return 'publish.fehler.unbekannt'

  if (grund === 'nothing_to_promote') return 'publish.fehler.nichts'
  if (grund === 'publish_busy') return 'publish.fehler.beschaeftigt'
  if (grund.startsWith('staging_check_failed')) return 'publish.fehler.pruefungFehlt'
  if (grund.startsWith('main is')) return 'publish.fehler.livevoraus'
  if (grund.startsWith('live_build_failed')) return 'publish.fehler.bauFehler'
  if (grund.startsWith('live_build_skipped')) return 'publish.fehler.ungesichert'
  if (grund.startsWith('backup_failed')) return 'publish.fehler.sicherung'
  // „push failed" kommt erst NACH dem Bau: Die Website ist dann schon draußen,
  // nur der Vermerk fehlt. Der Satz muss das sagen, sonst sucht jemand einen
  // Fehler auf einer Seite, die längst richtig ist.
  if (grund.startsWith('push failed')) return 'publish.fehler.freigabe'
  // Websites bei Cloudflare (seit 01.11.2026): Alle vier Fälle brechen ab,
  // bevor etwas draußen ist.
  if (grund.startsWith('cloudflare_nicht_eingerichtet')) return 'publish.fehler.cloudflareZugang'
  if (grund.startsWith('cloudflare_nicht_lesbar')) return 'publish.fehler.cloudflareLesen'
  if (grund.startsWith('cloudflare_hochladen_fehlgeschlagen')) return 'publish.fehler.hochladen'
  if (grund.startsWith('live_build_cmd_veraltet')) return 'publish.fehler.baubefehlAlt'

  return 'publish.fehler.unbekannt'
}

/** Übersetzungsschlüssel für ein gescheitertes Zurücknehmen. */
export function zuruecknehmenSchluessel(grund: string | null | undefined): string {
  if (!grund) return 'publish.zurueck.fehler.unbekannt'

  if (grund === 'rollback_not_possible') return 'publish.zurueck.fehler.nichtMoeglich'
  if (grund === 'no_backup' || grund === 'backup_not_found') return 'publish.zurueck.fehler.keine'
  if (grund === 'rollback_outdated') return 'publish.zurueck.fehler.veraltet'
  if (grund === 'publish_busy') return 'publish.fehler.beschaeftigt'
  if (grund === 'cloudflare_umschalten_fehlgeschlagen') return 'publish.zurueck.fehler.umschalten'
  if (grund.startsWith('cloudflare_nicht_lesbar')) return 'publish.zurueck.fehler.cloudflareLesen'
  if (grund.startsWith('cloudflare_nicht_eingerichtet')) return 'publish.fehler.cloudflareZugang'

  return 'publish.zurueck.fehler.unbekannt'
}

/**
 * Zeitpunkt einer Sicherung, wie er im Knopf steht: kurz und in der Sprache der
 * Oberfläche. Kommt Unsinn an, lieber nichts zeigen als „Invalid Date".
 */
export function standZeit(iso: string, sprache: string): string {
  const zeit = new Date(iso)
  if (Number.isNaN(zeit.getTime())) return ''
  return zeit.toLocaleString(sprache, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
